/**
 * SlackChannelsCard Component
 *
 * Slack notification channels card for settings page.
 * Supports both OAuth-based "Add to Slack" and manual webhook entry.
 *
 * Design Compliance:
 * - Single Card pattern (matches other settings cards)
 * - Icon + Title header with tooltip
 * - Uses Polaris spacing scale (gap="400", gap="500")
 * - Consistent channel item treatment using ChannelItem component
 */

import { useState, useCallback } from 'react';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  TextField,
  Button,
  Box,
  Icon,
  Modal,
  Collapsible,
  Tooltip,
  Divider,
} from '@shopify/polaris';
import { HashtagIcon, InfoIcon, ChevronUpIcon, ChevronDownIcon, PlusCircleIcon } from '@shopify/polaris-icons';
import { ChannelItem } from '../channel-item';
import {
  SlackConnectButton,
  SlackInstallationsList,
  useSlackInstallations
} from '../channel-item';
import { EmptyStateIllustration } from '../../ui/EmptyStateIllustration';
import { EmptyStateContainer } from '../../ui/EmptyStateContainer';
import type { NotificationChannel } from '../../dashboard/NotificationChannels';

export interface SlackChannelsCardProps {
  /** Slack notification channels (webhook-based) */
  channels: NotificationChannel[];
  /** Current shop domain for OAuth */
  shopDomain: string;
  /** Handler for testing a channel */
  onChannelTest: (channel: NotificationChannel) => void;
  /** Handler for toggling a channel */
  onChannelToggle: (channel: NotificationChannel) => void;
  /** Handler for deleting a channel */
  onChannelDelete: (channel: NotificationChannel) => void;
  /** Handler for adding a new Slack channel (webhook) */
  onChannelAdd: (name: string, webhookUrl: string) => Promise<void>;
  /** Callback when copy-to-clipboard succeeds */
  onCopySuccess?: (message: string) => void;
  /** Callback for toast messages */
  onToast?: (message: string, isError?: boolean) => void;
  /** Callback when OAuth installations change */
  onInstallationsChange?: () => void;
  /** Whether actions are loading */
  isLoading?: boolean;
  /** Whether this section is collapsible */
  isCollapsible?: boolean;
  /** Whether the section is open (only used when isCollapsible is true) */
  isOpen?: boolean;
  /** Handler for toggling the section (only used when isCollapsible is true) */
  onToggle?: () => void;
}

export function SlackChannelsCard({
  channels,
  shopDomain,
  onChannelTest,
  onChannelToggle,
  onChannelDelete,
  onChannelAdd,
  onCopySuccess,
  onToast,
  onInstallationsChange,
  isLoading = false,
  isCollapsible = false,
  isOpen = true,
  onToggle,
}: SlackChannelsCardProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [channelName, setChannelName] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [isAddingChannel, setIsAddingChannel] = useState(false);

  // Manage Slack installations state here to separate Button and List
  const {
    installations,
    isLoading: isSlackLoading,
    error: slackError,
    setError: setSlackError,
    fetchInstallations,
    handleTest,
    handleDisconnect,
    testingId,
    disconnectingId,
  } = useSlackInstallations(shopDomain, onInstallationsChange, onToast);

  const handleAddChannel = useCallback(async () => {
    if (!channelName.trim() || !webhookUrl.trim()) return;

    setIsAddingChannel(true);
    try {
      await onChannelAdd(channelName.trim(), webhookUrl.trim());
      setShowAddModal(false);
      setChannelName('');
      setWebhookUrl('');
    } finally {
      setIsAddingChannel(false);
    }
  }, [channelName, webhookUrl, onChannelAdd]);

  const closeAddModal = useCallback(() => {
    setShowAddModal(false);
    setChannelName('');
    setWebhookUrl('');
  }, []);

  const hasAnyConnection = installations.length > 0 || channels.length > 0;

  const content = (
    <Box paddingBlockStart="200">
      <BlockStack gap="400">
        {!hasAnyConnection && !isSlackLoading ? (
          <div className="empty-state-section empty-state-section--sm">
            <EmptyStateContainer size="sm">
              <BlockStack gap="200" align="center">
                <EmptyStateIllustration variant="slack" />
                <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                  No Slack connections configured
                </Text>
                <InlineStack align="center">
                  <SlackConnectButton
                    shopDomain={shopDomain}
                    onSuccess={() => {
                      fetchInstallations();
                      onInstallationsChange?.();
                    }}
                    onToast={onToast}
                  />
                </InlineStack>
              </BlockStack>
            </EmptyStateContainer>
          </div>
        ) : (
          <BlockStack gap="500">
            {/* OAuth Connect Section */}
            {(installations.length > 0 || isSlackLoading) && (
              <BlockStack gap="300">
                <SlackInstallationsList
                  installations={installations}
                  isLoading={isSlackLoading}
                  error={slackError}
                  onErrorDismiss={() => setSlackError(null)}
                  testingId={testingId}
                  disconnectingId={disconnectingId}
                  onTest={handleTest}
                  onDisconnect={handleDisconnect}
                  hideEmptyState
                />
                <InlineStack align="start">
                  <SlackConnectButton
                    shopDomain={shopDomain}
                    onSuccess={() => {
                      fetchInstallations();
                      onInstallationsChange?.();
                    }}
                    onToast={onToast}
                  />
                </InlineStack>
              </BlockStack>
            )}

            {/* Webhook Channels Section (Existing only) */}
            {channels.length > 0 && (
              <BlockStack gap="300">
                {installations.length > 0 && <Divider />}
                <BlockStack gap="100">
                  <Text as="h3" variant="headingSm">
                    Webhook Channels
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Manual webhook-based Slack integrations
                  </Text>
                </BlockStack>
                <BlockStack gap="300">
                  {channels.map((channel) => (
                    <ChannelItem
                      key={channel.id}
                      channel={channel}
                      onTest={onChannelTest}
                      onToggle={onChannelToggle}
                      onDelete={onChannelDelete}
                      onCopySuccess={onCopySuccess}
                      isLoading={isLoading}
                    />
                  ))}
                </BlockStack>
              </BlockStack>
            )}
          </BlockStack>
        )}
      </BlockStack>
    </Box>
  );

  const header = isCollapsible ? (
    <InlineStack align="space-between" blockAlign="start">
      <InlineStack gap="200" blockAlign="start">
        <Box paddingBlockStart="050">
          <Icon source={HashtagIcon} tone="subdued" />
        </Box>
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd">
            Slack
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            Connect to your workspace to receive alerts in Slack.
          </Text>
        </BlockStack>
      </InlineStack>
      <Box paddingBlockStart="050">
        <Icon source={isOpen ? ChevronUpIcon : ChevronDownIcon} tone="subdued" />
      </Box>
    </InlineStack>
  ) : (
    <InlineStack align="space-between" blockAlign="start">
      <InlineStack gap="200" blockAlign="start">
        <Box paddingBlockStart="050">
          <Icon source={HashtagIcon} tone="subdued" />
        </Box>
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd">
            Slack
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            Connect to your workspace to receive alerts in Slack.
          </Text>
        </BlockStack>
      </InlineStack>

    </InlineStack>
  );

  return (
    <>
      <Card>
        <BlockStack gap="400">
          {/* Header */}
          {isCollapsible ? (
            <button
              onClick={onToggle}
              aria-expanded={isOpen}
              aria-controls="section-slack-channels"
              style={{
                width: '100%',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              {header}
            </button>
          ) : (
            header
          )}

          {/* Content */}
          {isCollapsible ? (
            <Collapsible
              open={isOpen}
              id="section-slack-channels"
              transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
            >
              {content}
            </Collapsible>
          ) : (
            content
          )}
        </BlockStack>
      </Card>

      {/* Add Slack Channel Modal */}
      <Modal
        open={showAddModal}
        onClose={closeAddModal}
        title="Add Slack channel"
        primaryAction={{
          content: 'Add Slack',
          onAction: handleAddChannel,
          loading: isAddingChannel,
          disabled: !channelName.trim() || !webhookUrl.trim(),
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: closeAddModal,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Channel name"
              value={channelName}
              onChange={setChannelName}
              placeholder="e.g., Team Alerts, Operations"
              autoComplete="off"
              helpText="A friendly name to identify this channel"
            />

            <TextField
              label="Slack webhook URL"
              value={webhookUrl}
              onChange={setWebhookUrl}
              placeholder="https://hooks.slack.com/services/..."
              autoComplete="off"
              helpText="Get this from your Slack app Incoming Webhooks settings"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
}